import {
  TAG_CATEGORY_INFO,
  tagCategoryInfo,
  type SemanticTag,
  type TagCategory,
  type TagEmphasis,
  type TagTarget,
} from '@/semantics/SemanticTag';
import { formatBytes, tagImageIntentInfo, type TagImage } from '@/semantics/TagImage';
import type { TagStore } from '@/semantics/TagStore';
import { clear, el } from './dom';
import { icon, type IconName } from './Icons';
import {
  buildTagIndex,
  filterTags,
  formatTagDate,
  sectionTags,
  summariseTagIndex,
  tagsCsv,
  targetDetail,
  type TagGrouping,
  type TagIndex,
  type TagIndexEntry,
} from './TagIndex';

/**
 * The tag index: everything this site has been told, in one sheet.
 *
 * ## Why this is a reading surface and not another editor
 *
 * Tags are already writable in two places — the bubble that opens off a click, and the semantics
 * block in the inspector — and both are anchored to a *thing*, which is exactly right for writing one
 * and exactly wrong for auditing forty. What has never existed is the whole-document view: the
 * contradiction between a "keep out" on one bed and a planting on the same bed is invisible until
 * both lines are on screen together, and a photograph attached three sessions ago is unrecallable
 * until it is shown at a size you can actually judge.
 *
 * So this panel reads and does not write. That is a deliberate narrowing, not an omission: it keeps
 * the sheet safe to leave open over a live scene, and it keeps one tag's truth in one editor rather
 * than in three that have to be kept agreeing.
 *
 * ## Master and detail, not a table
 *
 * The manifest is a table because its rows are commensurable — every plant has a height, and reading
 * down the column is the point. A tag's payload is a sentence, a note of up to two more, and up to
 * three photographs; none of that survives being squeezed into a row, and a table of truncated
 * labels would answer none of the questions the panel exists for. So the list is the index and the
 * pane beside it is the tag, at full text and full picture.
 *
 * ## Where it sits
 *
 * Toggled from the canvas column's top-left corner row, alongside the manifest, because it is the
 * same kind of act: reading the site as a document rather than working on it. The sheet itself
 * floats centred over the view, non-modal, with the scene still live behind it — revealing what a
 * tag is attached to is a click, and a backdrop would hide the thing the click was for.
 */

export interface TagIndexPanelDeps {
  tags: TagStore;
  /** Human name for a target — supplied by the app, which is the only thing that knows the worlds. */
  describeTarget: (target: TagTarget) => string;
  /** Lengths follow the rest of the HUD. */
  unit?: () => 'meters' | 'feet';
  /**
   * Select and frame what a tag is about. Given it, the detail pane gains a **Show me** button;
   * omitted, the button does not exist — a dead control is worse than an absent one, the rule the
   * manifest's Ecology tab and its resolver already follow.
   *
   * Returns false when the target could not be reached (a ground material or the site as a whole has
   * nowhere to frame on), so the panel can say so instead of appearing to do nothing.
   */
  onReveal?: (tag: SemanticTag) => boolean;
}

export class TagIndexPanel {
  /** The toggle, for mounting into the canvas column's top-left corner row. */
  readonly bar: HTMLElement;
  /** The sheet's overlay, mounted over the canvas column. */
  readonly root: HTMLElement;

  private grouping: TagGrouping = 'subject';
  private query = '';
  private picturesOnly = false;
  private category: TagCategory | null = null;
  private selectedId: string | null = null;
  /** Rebuilt lazily — any tag edit invalidates it, but only an open sheet pays to recompute. */
  private cache: TagIndex | null = null;
  private frameHandle: number | null = null;

  private readonly openButton: HTMLButtonElement;
  private readonly countChip: HTMLElement;
  private readonly sheet: HTMLElement;
  private readonly subtitle: HTMLElement;
  private readonly groupTabs: HTMLElement;
  private readonly searchInput: HTMLInputElement;
  private readonly categorySelect: HTMLSelectElement;
  private readonly picturesToggle: HTMLInputElement;
  private readonly list: HTMLElement;
  private readonly detail: HTMLElement;
  private readonly note: HTMLElement;
  private readonly lightbox: HTMLElement;
  private readonly lightboxFigure: HTMLElement;
  private readonly unsubscribe: (() => void)[] = [];
  private noteTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Escape, unwound one layer at a time: the enlarged picture, then a filter that is hiding tags,
   * then the sheet. Each press undoes the last thing that narrowed what you can see — the same
   * ordering the manifest uses, so the key means one thing across both documents.
   */
  private readonly onWindowKeyDown = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape') return;
    if (!this.isOpen) return;
    event.stopPropagation();
    event.preventDefault();
    if (!this.lightbox.hidden) {
      this.closeLightbox();
      return;
    }
    if (this.query !== '' || this.picturesOnly || this.category !== null) {
      this.clearFilters();
      return;
    }
    this.close();
  };

  constructor(private readonly deps: TagIndexPanelDeps) {
    this.countChip = el('span', { class: 'lg-tagindex__count', text: '0' });
    this.openButton = el('button', {
      class: 'lg-tagindex__open',
      attrs: { type: 'button', 'aria-expanded': 'false' },
      title: 'Tags — everything this site has been told, with the pictures attached to it',
      on: { click: () => this.toggle() },
    });
    this.openButton.append(
      el('span', { class: 'lg-tagindex__openicon', html: icon('tag', 15) }),
      el('span', { class: 'lg-tagindex__label', text: 'Tags' }),
      this.countChip,
    );
    this.bar = el('div', { class: 'lg-tagindex__bar' }, this.openButton);

    this.subtitle = el('span', { class: 'lg-tagindex__subtitle' });
    this.groupTabs = el('div', {
      class: 'lg-segment lg-segment--tight',
      attrs: { role: 'group', 'aria-label': 'Group the tags' },
    });
    this.list = el('div', {
      class: 'lg-tagindex__list',
      attrs: { role: 'listbox', 'aria-label': 'Semantic tags', tabindex: '0' },
      on: { keydown: (event) => this.onListKeyDown(event) },
    });
    this.detail = el('div', { class: 'lg-tagindex__detail' });
    this.note = el('span', { class: 'lg-tagindex__note' });

    this.searchInput = el('input', {
      class: 'lg-tagindex__search',
      attrs: {
        type: 'search',
        placeholder: 'Filter by wording, subject, or caption…',
        'aria-label': 'Filter the tags',
        spellcheck: 'false',
      },
      on: {
        input: () => {
          this.query = this.searchInput.value;
          this.render();
        },
      },
    });

    this.categorySelect = el('select', {
      class: 'lg-tagindex__select',
      attrs: { 'aria-label': 'Show one category' },
      on: {
        change: () => {
          const value = this.categorySelect.value;
          this.category = value === '' ? null : (value as TagCategory);
          this.render();
        },
      },
    });
    this.categorySelect.appendChild(el('option', { text: 'All kinds', attrs: { value: '' } }));
    for (const info of TAG_CATEGORY_INFO) {
      this.categorySelect.appendChild(
        el('option', { text: info.name, title: info.hint, attrs: { value: info.id } }),
      );
    }

    this.picturesToggle = el('input', {
      attrs: { type: 'checkbox' },
      on: {
        change: () => {
          this.picturesOnly = this.picturesToggle.checked;
          this.render();
        },
      },
    });

    this.lightboxFigure = el('figure', { class: 'lg-tagindex__lightfig' });
    this.lightbox = this.buildLightbox();
    this.sheet = this.buildSheet();
    this.root = el('div', { class: 'lg-tagindex' }, this.sheet, this.lightbox);

    // Escape is claimed on `window` in the capture phase for the reason the manifest claims it there:
    // rebuilding the list replaces the element that had focus, so a listener hung off a row would
    // never see the key — and capture puts it ahead of the editor's own handler, so the press closes
    // the sheet instead of clearing the selection behind it.
    if (typeof window !== 'undefined') {
      window.addEventListener('keydown', this.onWindowKeyDown, true);
      this.unsubscribe.push(() => window.removeEventListener('keydown', this.onWindowKeyDown, true));
    }

    this.unsubscribe.push(deps.tags.onChange(() => this.invalidate()));

    this.setGrouping('subject');
    this.refreshCount();
  }

  get isOpen(): boolean {
    return this.sheet.hidden === false;
  }

  toggle(): void {
    if (this.isOpen) this.close();
    else this.open();
  }

  open(): void {
    this.sheet.hidden = false;
    this.openButton.setAttribute('aria-expanded', 'true');
    this.openButton.classList.add('is-open');
    this.render();
    this.searchInput.focus();
  }

  close(): void {
    this.closeLightbox();
    this.sheet.hidden = true;
    this.openButton.setAttribute('aria-expanded', 'false');
    this.openButton.classList.remove('is-open');
    this.openButton.focus();
  }

  /**
   * Open the sheet with one tag already selected — the way in from a marker or an inspector row.
   *
   * Kept public so a future "show this tag in the index" affordance has somewhere to land; the panel
   * is a reading surface, and being able to arrive at the right page of it is most of what that is
   * worth.
   */
  reveal(tagId: string): void {
    this.selectedId = tagId;
    if (!this.isOpen) this.open();
    else this.render();
  }

  /** Drop the cached index and redraw whatever is on screen. */
  invalidate(): void {
    this.cache = null;
    this.requestRender();
  }

  private requestRender(): void {
    if (this.frameHandle !== null) return;
    const schedule =
      typeof requestAnimationFrame !== 'undefined'
        ? requestAnimationFrame
        : (callback: FrameRequestCallback): number =>
            setTimeout(() => callback(0), 16) as unknown as number;
    this.frameHandle = schedule(() => {
      this.frameHandle = null;
      this.refreshCount();
      if (this.isOpen) this.render();
    });
  }

  /** The index for the current document, built at most once per edit. */
  private index(): TagIndex {
    this.cache ??= buildTagIndex({
      tags: this.deps.tags.all(),
      describeTarget: (target) => this.deps.describeTarget(target),
    });
    return this.cache;
  }

  private get unit(): 'meters' | 'feet' {
    return this.deps.unit?.() ?? 'meters';
  }

  /* --------------------------------- Construction --------------------------------- */

  private buildSheet(): HTMLElement {
    const groups: readonly (readonly [TagGrouping, string, string])[] = [
      ['subject', 'By subject', 'Grouped by what each tag is about, widest claim first'],
      ['category', 'By kind', 'Grouped by what sort of statement each tag makes'],
    ];
    for (const [grouping, label, hint] of groups) {
      this.groupTabs.appendChild(
        el('button', {
          class: 'lg-segment__item',
          text: label,
          title: hint,
          attrs: { type: 'button', 'data-group': grouping },
          on: { click: () => this.setGrouping(grouping) },
        }),
      );
    }

    return el(
      'div',
      {
        class: 'lg-tagindex__sheet',
        attrs: { role: 'dialog', 'aria-label': 'Semantic tags', hidden: true },
      },
      el(
        'header',
        { class: 'lg-tagindex__head' },
        el('span', { class: 'lg-tagindex__headicon', html: icon('tag', 16) }),
        el(
          'div',
          { class: 'lg-tagindex__heading' },
          el('h2', { class: 'lg-tagindex__title', text: 'Semantic tags' }),
          this.subtitle,
        ),
        this.groupTabs,
        el('button', {
          class: 'lg-tagindex__iconbutton',
          html: icon('close', 15),
          title: 'Close  ·  Esc',
          attrs: { type: 'button', 'aria-label': 'Close the tag index' },
          on: { click: () => this.close() },
        }),
      ),
      el(
        'div',
        { class: 'lg-tagindex__tools' },
        el(
          'div',
          { class: 'lg-tagindex__searchwrap' },
          el('span', { class: 'lg-tagindex__searchicon', html: icon('search', 14) }),
          this.searchInput,
        ),
        this.categorySelect,
        el(
          'label',
          { class: 'lg-tagindex__check', title: 'Only the tags carrying reference photographs' },
          this.picturesToggle,
          el('span', { text: 'With pictures' }),
        ),
        el('div', { class: 'lg-tagindex__spacer' }),
        this.actionButton('duplicate', 'Copy', 'Copy this list to the clipboard as CSV', () => {
          void this.copyCsv();
        }),
        this.actionButton('download', 'CSV', 'Download this list as a CSV file', () =>
          this.downloadCsv(),
        ),
      ),
      el('div', { class: 'lg-tagindex__body' }, this.list, this.detail),
      el('footer', { class: 'lg-tagindex__footer' }, this.note),
    );
  }

  private buildLightbox(): HTMLElement {
    return el(
      'div',
      {
        class: 'lg-tagindex__light',
        attrs: { hidden: true, role: 'dialog', 'aria-label': 'Reference photograph' },
        on: {
          click: (event) => {
            // Only the scrim closes it — a click on the picture itself is someone looking closer.
            if (event.target === event.currentTarget) this.closeLightbox();
          },
        },
      },
      el(
        'div',
        { class: 'lg-tagindex__lightbox' },
        el('button', {
          class: 'lg-tagindex__lightclose',
          html: icon('close', 16),
          title: 'Close  ·  Esc',
          attrs: { type: 'button', 'aria-label': 'Close the photograph' },
          on: { click: () => this.closeLightbox() },
        }),
        this.lightboxFigure,
      ),
    );
  }

  private actionButton(
    iconName: IconName,
    label: string,
    title: string,
    onClick: () => void,
  ): HTMLElement {
    return el(
      'button',
      { class: 'lg-tagindex__action', title, attrs: { type: 'button' }, on: { click: onClick } },
      el('span', { class: 'lg-tagindex__actionicon', html: icon(iconName, 14) }),
      el('span', { text: label }),
    );
  }

  private setGrouping(grouping: TagGrouping): void {
    this.grouping = grouping;
    for (const button of this.groupTabs.querySelectorAll('button')) {
      button.classList.toggle('is-active', button.dataset.group === grouping);
    }
    if (this.isOpen) this.render();
  }

  private clearFilters(): void {
    this.query = '';
    this.searchInput.value = '';
    this.picturesOnly = false;
    this.picturesToggle.checked = false;
    this.category = null;
    this.categorySelect.value = '';
    this.render();
  }

  /* ----------------------------------- Rendering ----------------------------------- */

  /** The bar's live count, which is on screen whether or not the sheet is. */
  private refreshCount(): void {
    const count = this.deps.tags.count;
    this.countChip.textContent = String(count);
    this.countChip.hidden = count === 0;
  }

  private render(): void {
    const index = this.index();
    this.subtitle.textContent = summariseTagIndex(index);

    const visible = filterTags(index.entries, {
      query: this.query,
      picturesOnly: this.picturesOnly,
      category: this.category,
    });

    // A selection that has been filtered away — or undone out of the document — would leave the pane
    // describing a tag the list no longer shows, so it falls back to the first row that is on screen.
    if (!visible.some((entry) => entry.tag.id === this.selectedId)) {
      this.selectedId = visible[0]?.tag.id ?? null;
    }

    this.renderList(visible, index);
    this.renderDetail(visible.find((entry) => entry.tag.id === this.selectedId) ?? null);
    this.setNote(this.standingNote(index, visible.length));
  }

  private renderList(visible: readonly TagIndexEntry[], index: TagIndex): void {
    clear(this.list);
    if (visible.length === 0) {
      this.list.appendChild(
        el('p', {
          class: 'lg-tagindex__empty',
          text:
            index.totalTags === 0
              ? 'Nothing tagged yet. Pick up the tag tool and say what something is — it will be listed here, and carried into every render.'
              : 'No tag matches these filters.',
        }),
      );
      return;
    }

    for (const section of sectionTags(visible, this.grouping)) {
      this.list.appendChild(
        el(
          'div',
          { class: 'lg-tagindex__sectionhead' },
          el('span', { text: section.title }),
          el('span', { class: 'lg-tagindex__sectioncount', text: String(section.entries.length) }),
        ),
      );
      for (const entry of section.entries) this.list.appendChild(this.row(entry));
    }
  }

  private row(entry: TagIndexEntry): HTMLElement {
    const { tag } = entry;
    const info = tagCategoryInfo(tag.category);
    const selected = tag.id === this.selectedId;

    const meta = el('div', { class: 'lg-tagindex__rowmeta' });
    // In the subject grouping the category is the fact the swatch alone cannot spell; in the category
    // grouping it is the heading above, and what the reader needs instead is what it is attached to.
    meta.appendChild(
      el('span', {
        class: 'lg-tagindex__rowsubject',
        text: this.grouping === 'subject' ? info.name : entry.targetName,
      }),
    );
    if (tag.emphasis !== 'mention') {
      meta.appendChild(
        el('span', {
          class: `lg-tagindex__emphasis is-${tag.emphasis}`,
          text: emphasisLabel(tag.emphasis),
        }),
      );
    }
    if (entry.images.length > 0) {
      meta.appendChild(
        el(
          'span',
          {
            class: 'lg-tagindex__pics',
            title: `${entry.images.length} picture${entry.images.length === 1 ? '' : 's'}, ${formatBytes(entry.imageBytes)}`,
          },
          el('span', { class: 'lg-tagindex__picsicon', html: icon('gallery', 11) }),
          el('span', { text: String(entry.images.length) }),
        ),
      );
    }

    const row = el(
      'button',
      {
        class: `lg-tagindex__row${selected ? ' is-selected' : ''}`,
        attrs: {
          type: 'button',
          role: 'option',
          'aria-selected': selected ? 'true' : 'false',
          'data-tag': tag.id,
        },
        style: { ['--tag-color' as string]: info.color },
        on: {
          click: () => this.select(tag.id),
          dblclick: () => {
            this.select(tag.id);
            this.reveal_(tag);
          },
        },
      },
      el('span', { class: 'lg-tagindex__swatch' }),
      el(
        'span',
        { class: 'lg-tagindex__rowtext' },
        el('span', { class: 'lg-tagindex__rowlabel', text: tag.label }),
        meta,
      ),
    );
    return row;
  }

  /** The tag itself: full wording, full note, and the photographs at a size worth judging. */
  private renderDetail(entry: TagIndexEntry | null): void {
    clear(this.detail);
    if (!entry) {
      this.detail.appendChild(
        el('p', {
          class: 'lg-tagindex__empty',
          text: 'Select a tag to read it in full.',
        }),
      );
      return;
    }

    const { tag } = entry;
    const info = tagCategoryInfo(tag.category);
    this.detail.style.setProperty('--tag-color', info.color);

    const head = el(
      'div',
      { class: 'lg-tagindex__detailhead' },
      el('h3', { class: 'lg-tagindex__detaillabel', text: tag.label }),
      el(
        'div',
        { class: 'lg-tagindex__chips' },
        el('span', { class: 'lg-tagindex__chip is-category', text: info.name, title: info.hint }),
        el('span', {
          class: `lg-tagindex__chip is-emphasis is-${tag.emphasis}`,
          text: emphasisLabel(tag.emphasis),
          title: emphasisHint(tag.emphasis),
        }),
      ),
    );
    this.detail.appendChild(head);

    const detailText = targetDetail(tag, this.unit);
    this.detail.appendChild(
      this.field(
        'Attached to',
        el(
          'div',
          { class: 'lg-tagindex__target' },
          el('span', { class: 'lg-tagindex__targetname', text: entry.targetName }),
          detailText !== '' ? el('span', { class: 'lg-tagindex__targetdetail', text: detailText }) : null,
        ),
      ),
    );

    this.detail.appendChild(
      this.field(
        'Note',
        el('p', {
          class: `lg-tagindex__notetext${tag.note === undefined ? ' is-empty' : ''}`,
          text: tag.note ?? 'No note. The label is the whole of what this tag says.',
        }),
      ),
    );

    this.detail.appendChild(this.picturesBlock(entry));

    const added = formatTagDate(tag.createdAt);
    const footer = el('div', { class: 'lg-tagindex__detailfoot' });
    if (added !== '') footer.appendChild(el('span', { text: `Added ${added}` }));
    if (this.deps.onReveal) {
      footer.appendChild(
        el(
          'button',
          {
            class: 'lg-tagindex__reveal',
            title: 'Select what this tag is about and frame the camera on it',
            attrs: { type: 'button' },
            on: { click: () => this.reveal_(tag) },
          },
          el('span', { class: 'lg-tagindex__actionicon', html: icon('focus', 14) }),
          el('span', { text: 'Show me' }),
        ),
      );
    }
    this.detail.appendChild(footer);
  }

  /**
   * The attached photographs, shown rather than counted.
   *
   * Each carries its intent above it, because a picture on a tag is meaningless without one — "build
   * it like this" and "this feeling" ask the renderer for opposite things from the same JPEG, and a
   * gallery that dropped that distinction would be a gallery you could not act on.
   */
  private picturesBlock(entry: TagIndexEntry): HTMLElement {
    if (entry.images.length === 0) {
      return this.field(
        'Pictures',
        el('p', { class: 'lg-tagindex__notetext is-empty', text: 'None attached.' }),
      );
    }

    const grid = el('div', { class: 'lg-tagindex__gallery' });
    for (const image of entry.images) grid.appendChild(this.picture(entry, image));
    return this.field(`Pictures  ·  ${formatBytes(entry.imageBytes)}`, grid);
  }

  private picture(entry: TagIndexEntry, image: TagImage): HTMLElement {
    const intent = tagImageIntentInfo(image.intent);
    const dimensions =
      image.widthPx > 0 && image.heightPx > 0 ? `${image.widthPx} × ${image.heightPx}` : '';

    return el(
      'figure',
      { class: 'lg-tagindex__picture' },
      el(
        'button',
        {
          class: 'lg-tagindex__picbutton',
          title: 'Open this photograph full size',
          attrs: { type: 'button' },
          on: { click: () => this.openLightbox(entry, image) },
        },
        el('img', {
          class: 'lg-tagindex__picimg',
          attrs: {
            src: image.dataUrl,
            alt: image.caption ?? `${intent.name} — ${entry.tag.label}`,
            loading: 'lazy',
            decoding: 'async',
          },
        }),
        el('span', { class: 'lg-tagindex__picexpand', html: icon('expand', 13) }),
      ),
      el(
        'figcaption',
        { class: 'lg-tagindex__piccap' },
        el('span', { class: 'lg-tagindex__picintent', text: intent.name, title: intent.hint }),
        image.caption !== undefined
          ? el('span', { class: 'lg-tagindex__piccaption', text: image.caption })
          : null,
        el('span', {
          class: 'lg-tagindex__picmeta',
          text: [dimensions, formatBytes(image.bytes), image.name ?? ''].filter(Boolean).join('  ·  '),
        }),
      ),
    );
  }

  private field(label: string, body: HTMLElement): HTMLElement {
    return el(
      'section',
      { class: 'lg-tagindex__field' },
      el('h4', { class: 'lg-tagindex__fieldlabel', text: label }),
      body,
    );
  }

  /* ------------------------------------ Lightbox ------------------------------------ */

  private openLightbox(entry: TagIndexEntry, image: TagImage): void {
    const intent = tagImageIntentInfo(image.intent);
    clear(this.lightboxFigure);
    this.lightboxFigure.append(
      el('img', {
        class: 'lg-tagindex__lightimg',
        attrs: { src: image.dataUrl, alt: image.caption ?? entry.tag.label },
      }),
      el(
        'figcaption',
        { class: 'lg-tagindex__lightcap' },
        el('strong', { text: entry.tag.label }),
        el('span', { text: `${intent.name}${image.caption !== undefined ? ` — ${image.caption}` : ''}` }),
        el('span', {
          class: 'lg-tagindex__picmeta',
          text: [
            image.widthPx > 0 ? `${image.widthPx} × ${image.heightPx}` : '',
            formatBytes(image.bytes),
          ]
            .filter(Boolean)
            .join('  ·  '),
        }),
      ),
    );
    this.lightbox.hidden = false;
  }

  private closeLightbox(): void {
    if (this.lightbox.hidden) return;
    this.lightbox.hidden = true;
    clear(this.lightboxFigure);
  }

  /* ---------------------------------- Wiring out ---------------------------------- */

  private select(tagId: string): void {
    if (this.selectedId === tagId) return;
    this.selectedId = tagId;
    this.render();
  }

  /**
   * Arrow keys walk the list.
   *
   * Worth the handler: this panel is read by scanning, and a list you can only move through by
   * clicking is one you check the first three rows of.
   */
  private onListKeyDown(event: KeyboardEvent): void {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const rows = [...this.list.querySelectorAll<HTMLElement>('.lg-tagindex__row')];
    if (rows.length === 0) return;
    event.preventDefault();
    const current = rows.findIndex((row) => row.dataset.tag === this.selectedId);
    const step = event.key === 'ArrowDown' ? 1 : -1;
    const next = rows[Math.min(rows.length - 1, Math.max(0, current + step))];
    const id = next?.dataset.tag;
    if (id !== undefined) {
      this.select(id);
      this.list
        .querySelector<HTMLElement>(`.lg-tagindex__row[data-tag="${id}"]`)
        ?.scrollIntoView({ block: 'nearest' });
    }
  }

  /** Trailing underscore only because `reveal` is the public "open me on this tag" entry point. */
  private reveal_(tag: SemanticTag): void {
    const reveal = this.deps.onReveal;
    if (!reveal) return;
    if (!reveal(tag)) {
      this.flashNote(
        tag.target.kind === 'ground'
          ? 'That tag is about a material, not a place — it applies everywhere the material is painted.'
          : 'That tag is about the site as a whole, so there is nothing to frame on.',
      );
    }
  }

  /* -------------------------------------- Export -------------------------------------- */

  /** CSV for what is on screen, filters honoured — you export what you are reading. */
  private csv(): string {
    return tagsCsv(
      filterTags(this.index().entries, {
        query: this.query,
        picturesOnly: this.picturesOnly,
        category: this.category,
      }),
    );
  }

  private async copyCsv(): Promise<void> {
    try {
      await navigator.clipboard?.writeText(this.csv());
      this.flashNote('Copied to the clipboard.');
    } catch {
      this.flashNote('Could not reach the clipboard — use the CSV download instead.');
    }
  }

  private downloadCsv(): void {
    const blob = new Blob([this.csv()], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = el('a', {
      attrs: { href: url, download: 'semantic-tags.csv' },
      style: { display: 'none' },
    });
    this.root.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
    this.flashNote('Exported semantic-tags.csv');
  }

  private standingNote(index: TagIndex, shown: number): string {
    if (index.totalTags === 0) return 'Tags are carried into every render, and into every export.';
    if (shown < index.totalTags) return `Showing ${shown} of ${index.totalTags} tags.`;
    return 'Every tag here is stated in the prompt of each render this site produces.';
  }

  private setNote(text: string): void {
    if (this.noteTimer !== null) {
      clearTimeout(this.noteTimer);
      this.noteTimer = null;
    }
    this.note.textContent = text;
  }

  /** Say something in the footer, then let the standing summary take the line back. */
  private flashNote(message: string): void {
    this.setNote(message);
    this.noteTimer = setTimeout(() => {
      this.noteTimer = null;
      if (this.isOpen) this.render();
    }, 3200);
  }

  dispose(): void {
    if (this.frameHandle !== null && typeof cancelAnimationFrame !== 'undefined') {
      cancelAnimationFrame(this.frameHandle);
    }
    this.frameHandle = null;
    if (this.noteTimer !== null) clearTimeout(this.noteTimer);
    this.noteTimer = null;
    for (const off of this.unsubscribe) off();
    this.unsubscribe.length = 0;
    this.bar.remove();
    this.root.remove();
  }
}

/* -------------------------------------- Helpers -------------------------------------- */

function emphasisLabel(emphasis: TagEmphasis): string {
  switch (emphasis) {
    case 'critical':
      return 'Essential';
    case 'emphasise':
      return 'Emphasised';
    default:
      return 'Mentioned';
  }
}

function emphasisHint(emphasis: TagEmphasis): string {
  switch (emphasis) {
    case 'critical':
      return 'The render is wrong without it.';
    case 'emphasise':
      return 'Asked for clearly, every time.';
    default:
      return 'Stated once, in passing.';
  }
}
